import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import CollectionService from '../services/CollectionService';
import { slugFromName } from '../utils/categoryTree';

const MONTHS = [
  { value: 1, label: 'Janv.' },
  { value: 2, label: 'Févr.' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avr.' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juil.' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Sept.' },
  { value: 10, label: 'Oct.' },
  { value: 11, label: 'Nov.' },
  { value: 12, label: 'Déc.' },
];

const SEO_LEVELS = [
  { value: 'A', label: 'A — Indexation prioritaire' },
  { value: 'B', label: 'B — Indexable' },
  { value: 'C', label: 'C — noindex (défaut)' },
];

const emptyForm = () => ({
  title: '',
  slug: '',
  subtitle: '',
  description: '',
  emoji: '',
  type: 'theme',
  seoLevel: 'C',
  metaTitle: '',
  metaDescription: '',
  published: true,
  showOnHome: false,
  homePosition: 0,
  activeMonths: [],
  startsAt: '',
  endsAt: '',
  matchTags: '',
  matchCategoryNames: '',
  productIds: '',
});

const listToText = (values) => (Array.isArray(values) ? values.join(', ') : '');
const idsToText = (values) => (Array.isArray(values) ? values.join('\n') : '');

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);

  const loadCollections = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await CollectionService.list();
      setCollections(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Impossible de charger les collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollections();
  }, []);

  const fillForm = (collection) => {
    setForm({
      title: collection.title || '',
      slug: collection.slug || '',
      subtitle: collection.subtitle || '',
      description: collection.description || '',
      emoji: collection.emoji || '',
      type: collection.type || 'theme',
      seoLevel: collection.seoLevel || 'C',
      metaTitle: collection.metaTitle || '',
      metaDescription: collection.metaDescription || '',
      published: collection.published !== false,
      showOnHome: Boolean(collection.showOnHome),
      homePosition: collection.homePosition ?? 0,
      activeMonths: Array.isArray(collection.activeMonths) ? collection.activeMonths : [],
      startsAt: collection.startsAt || '',
      endsAt: collection.endsAt || '',
      matchTags: listToText(collection.matchTags),
      matchCategoryNames: listToText(collection.matchCategoryNames),
      productIds: idsToText(collection.productIds),
    });
    setSlugTouched(true);
  };

  const handleSelect = (collection) => {
    setSelectedId(collection.id);
    fillForm(collection);
  };

  const handleNew = () => {
    setSelectedId(null);
    setForm(emptyForm());
    setSlugTouched(false);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: type === 'checkbox' ? checked : value };
      if (name === 'title' && !selectedId && !slugTouched) {
        next.slug = slugFromName(value);
      }
      return next;
    });
    if (name === 'slug') {
      setSlugTouched(true);
    }
  };

  const toggleMonth = (month) => {
    setForm((current) => {
      const has = current.activeMonths.includes(month);
      return {
        ...current,
        activeMonths: has
          ? current.activeMonths.filter((value) => value !== month)
          : [...current.activeMonths, month].sort((a, b) => a - b),
      };
    });
  };

  const payloadFromForm = () => ({
    title: form.title.trim(),
    slug: form.slug.trim(),
    subtitle: form.subtitle,
    description: form.description,
    emoji: form.emoji.trim(),
    type: form.type,
    seoLevel: form.seoLevel,
    metaTitle: form.metaTitle,
    metaDescription: form.metaDescription,
    published: form.published,
    showOnHome: form.showOnHome,
    homePosition: Number(form.homePosition) || 0,
    activeMonths: form.activeMonths,
    startsAt: form.startsAt || null,
    endsAt: form.endsAt || null,
    matchTags: form.matchTags,
    matchCategoryNames: form.matchCategoryNames,
    productIds: form.productIds,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = payloadFromForm();
      if (selectedId) {
        const updated = await CollectionService.update(selectedId, payload);
        toast.success('Collection mise à jour');
        await loadCollections();
        if (updated) {
          setSelectedId(updated.id);
          fillForm(updated);
        }
      } else {
        const created = await CollectionService.save(payload);
        toast.success('Collection créée');
        await loadCollections();
        if (created) {
          setSelectedId(created.id);
          fillForm(created);
        }
      }
    } catch (err) {
      const message = err.response?.data?.error || 'Enregistrement impossible';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) {
      return;
    }
    if (!window.confirm('Supprimer cette collection ?')) {
      return;
    }
    setSaving(true);
    try {
      await CollectionService.delete(selectedId);
      toast.success('Collection supprimée');
      handleNew();
      await loadCollections();
    } catch (err) {
      const message = err.response?.data?.error || 'Suppression impossible';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Collections</h1>
          <p className="text-surface-400 mt-1">
            Thématiques, événements, accueil et niveau SEO
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={handleNew}>
          <AddIcon className="w-4 h-4" />
          Nouvelle collection
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 card p-4">
          <h2 className="text-sm font-medium text-surface-400 mb-3 px-2">Liste</h2>
          {loading ? (
            <div className="space-y-2 p-2">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="h-10 bg-surface-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : collections.length === 0 ? (
            <p className="text-surface-500 p-3 text-sm">Aucune collection pour l'instant.</p>
          ) : (
            <ul className="space-y-1">
              {collections.map((collection) => {
                const isActive = selectedId === collection.id;
                return (
                  <li key={collection.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(collection)}
                      aria-label={collection.title}
                      className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? 'bg-accent-500/15 text-white border border-accent-500/30'
                          : 'text-surface-200 hover:bg-surface-800/70'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <CollectionsBookmarkIcon className="w-4 h-4 text-surface-500" />
                        <span className="truncate">
                          {collection.emoji ? `${collection.emoji} ` : ''}
                          {collection.title}
                        </span>
                        <span className="ml-auto flex items-center gap-2 text-xs text-surface-500">
                          {collection.showOnHome ? 'accueil' : null}
                          <span aria-hidden="true">{collection.seoLevel}</span>
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="lg:col-span-3 card p-6">
          <h2 className="text-lg font-semibold text-white mb-6">
            {selectedId ? 'Modifier la collection' : 'Nouvelle collection'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="title" className="label">Titre</label>
              <input
                id="title"
                name="title"
                className="input"
                value={form.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="slug" className="label">Slug</label>
                <input
                  id="slug"
                  name="slug"
                  className="input"
                  value={form.slug}
                  onChange={handleChange}
                  readOnly={Boolean(selectedId)}
                  aria-describedby="slug-help"
                />
                <p id="slug-help" className="text-xs text-surface-500 mt-1.5">
                  {selectedId
                    ? 'Figé après création, pour conserver les URLs publiques.'
                    : 'Généré automatiquement à partir du titre, ajustable avant enregistrement.'}
                </p>
              </div>
              <div>
                <label htmlFor="emoji" className="label">Emoji</label>
                <input
                  id="emoji"
                  name="emoji"
                  className="input"
                  value={form.emoji}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="subtitle" className="label">Sous-titre</label>
              <input
                id="subtitle"
                name="subtitle"
                className="input"
                value={form.subtitle}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="description" className="label">Description</label>
              <textarea
                id="description"
                name="description"
                className="input min-h-[96px]"
                value={form.description}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="type" className="label">Type</label>
                <select id="type" name="type" className="input" value={form.type} onChange={handleChange}>
                  <option value="theme">Thématique</option>
                  <option value="event">Événementielle</option>
                </select>
              </div>
              <div>
                <label htmlFor="seoLevel" className="label">Niveau SEO</label>
                <select
                  id="seoLevel"
                  name="seoLevel"
                  className="input"
                  value={form.seoLevel}
                  onChange={handleChange}
                >
                  {SEO_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="metaTitle" className="label">Meta title</label>
              <input
                id="metaTitle"
                name="metaTitle"
                className="input"
                value={form.metaTitle}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="metaDescription" className="label">Meta description</label>
              <textarea
                id="metaDescription"
                name="metaDescription"
                className="input min-h-[72px]"
                value={form.metaDescription}
                onChange={handleChange}
              />
            </div>

            <fieldset className="space-y-3">
              <legend className="label">Accueil</legend>
              <label className="flex items-center gap-2 text-sm text-surface-200">
                <input
                  type="checkbox"
                  name="published"
                  checked={form.published}
                  onChange={handleChange}
                />
                Publiée
              </label>
              <label className="flex items-center gap-2 text-sm text-surface-200">
                <input
                  type="checkbox"
                  name="showOnHome"
                  checked={form.showOnHome}
                  onChange={handleChange}
                />
                Afficher sur l'accueil
              </label>
              <div>
                <label htmlFor="homePosition" className="label">Position sur l'accueil</label>
                <input
                  id="homePosition"
                  name="homePosition"
                  type="number"
                  className="input"
                  value={form.homePosition}
                  onChange={handleChange}
                />
              </div>
            </fieldset>

            <fieldset>
              <legend className="label">Mois actifs (vide = toute l'année)</legend>
              <div className="flex flex-wrap gap-2 mt-2">
                {MONTHS.map((month) => {
                  const checked = form.activeMonths.includes(month.value);
                  return (
                    <label
                      key={month.value}
                      className={`px-2 py-1 rounded-lg text-xs cursor-pointer border ${
                        checked
                          ? 'border-accent-500/50 bg-accent-500/15 text-white'
                          : 'border-surface-700 text-surface-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => toggleMonth(month.value)}
                      />
                      {month.label}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="startsAt" className="label">Début (optionnel)</label>
                <input
                  id="startsAt"
                  name="startsAt"
                  type="date"
                  className="input"
                  value={form.startsAt}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="endsAt" className="label">Fin (optionnel)</label>
                <input
                  id="endsAt"
                  name="endsAt"
                  type="date"
                  className="input"
                  value={form.endsAt}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="matchTags" className="label">Tags (séparés par des virgules)</label>
              <input
                id="matchTags"
                name="matchTags"
                className="input"
                value={form.matchTags}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="matchCategoryNames" className="label">
                Noms de catégories (virgules)
              </label>
              <input
                id="matchCategoryNames"
                name="matchCategoryNames"
                className="input"
                value={form.matchCategoryNames}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="productIds" className="label">Produits pinés (un identifiant par ligne)</label>
              <textarea
                id="productIds"
                name="productIds"
                className="input min-h-[88px] font-mono text-sm"
                value={form.productIds}
                onChange={handleChange}
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              {selectedId ? (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  <DeleteIcon className="w-4 h-4" />
                  Supprimer
                </button>
              ) : (
                <span />
              )}
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CollectionsPage;
